import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Trophy, Swords, TrendingUp, BookOpen, ChevronRight, Volume2, Ban } from "lucide-react";
import ReactionTest from "../components/ReactionTest";
import ResultReveal from "../components/ResultReveal";
import { getLocalBest } from "../lib/local";
import api from "../lib/api";
import Seo, { faqJsonLd, webAppJsonLd } from "../components/Seo";
import FeaturedGames from "../components/FeaturedGames";

const FAQ = [
  { q: "What is a good reaction time?", a: "On a browser-based visual test, many people land between 200–300 ms. Under 200 ms is very fast. Your result depends on hardware, input device, and focus — so treat it as a personal benchmark, not a fixed number." },
  { q: "Why do my results vary?", a: "Reaction time naturally fluctuates with attention, fatigue, screen refresh rate and input latency. That's why we run five rounds and report the median as well as the average." },
  { q: "Does this measure intelligence or athletic ability?", a: "No. ReactionLab measures performance on this specific browser task only. It is not a medical, neurological, intelligence, or athletic assessment." },
  { q: "Is it free?", a: "Yes. The core reaction test is completely free, no download and no account required to start." },
];

function TestCard({ to, icon: Icon, title, desc, active = true }) {
  return (
    <Link to={active ? to : "#"} className={`card-surface card-hover rounded-2xl p-6 block ${!active && "opacity-50 pointer-events-none"}`} data-testid={`lab-card-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="w-11 h-11 rounded-xl bg-[#39FF14]/10 flex items-center justify-center mb-4">
        <Icon className="text-[#39FF14]" size={22} />
      </div>
      <div className="font-display font-bold text-lg mb-1">{title}</div>
      <p className="text-white/45 text-sm">{desc}</p>
      {!active && <span className="text-xs text-white/30 mt-2 inline-block">Coming soon</span>}
    </Link>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [key, setKey] = useState(0);
  const [localBest, setLocalBest] = useState(null);
  const [top, setTop] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setLocalBest(getLocalBest());
    api.get("/leaderboard?period=all&sort=reaction").then(({ data }) => setTop(data.records.slice(0, 5))).catch(() => {});
    api.get("/statistics").then(({ data }) => setStats(data)).catch(() => {});
  }, [result]);

  const onFinished = useCallback((data, err) => {
    if (err || !data) return;
    setResult(data);
    setTimeout(() => document.getElementById("test-area")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, []);

  const tryAgain = () => { setResult(null); setKey((k) => k + 1); };

  return (
    <div className="hero-radial">
      <Seo
        title="ReactionLab — Reaction Time Test & Performance Platform"
        description="Test your reaction speed in milliseconds, see how you rank with your percentile, track progress, and challenge your friends. Free reaction time test, no download."
        jsonLd={[webAppJsonLd(), faqJsonLd(FAQ.map((f) => [f.q, f.a]))]}
      />
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-8 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/60 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" /> The reaction-performance playground
          </span>
          <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95]">
            How Fast Are<br /><span className="text-[#39FF14] glow-green">Your Reflexes?</span>
          </h1>
          <p className="text-white/60 max-w-xl mx-auto mt-6 text-base sm:text-lg">
            Test your reaction speed in milliseconds, see how you rank, and challenge your friends.
          </p>
          <p className="text-white/35 text-sm mt-3 font-mono">Free • No download • Desktop & mobile</p>
        </motion.div>
      </section>

      {/* TEST AREA */}
      <section id="test-area" className="max-w-3xl mx-auto px-4 sm:px-6 pb-6">
        <div className="min-h-[460px] flex items-center justify-center">
          {result ? (
            <ResultReveal result={result} onTryAgain={tryAgain} />
          ) : (
            <div className="w-full h-[460px]">
              <ReactionTest key={key} testType="classic" onFinished={onFinished} />
            </div>
          )}
        </div>
        {!result && (
          <p className="text-center text-white/35 text-sm mt-4">
            5-round test • Instant results • Personal statistics
            {localBest && <span className="text-white/50"> • Your best: <span className="text-[#39FF14] font-mono">{localBest}ms</span></span>}
          </p>
        )}
      </section>

      {/* STATS STRIP */}
      {stats && stats.totals && stats.totals.reaction_tests > 0 && (() => {
        const classic = stats.reaction.find((r) => r.test_type === "classic" && r.sample_size > 0) || stats.reaction.find((r) => r.sample_size > 0);
        return (
          <section className="max-w-6xl mx-auto px-4 sm:px-6">
            <Link to="/statistics" data-testid="home-stats-strip"
              className="card-surface card-hover rounded-2xl px-6 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center">
              <span className="text-sm text-white/60"><span className="font-mono font-bold text-[#39FF14] text-base">{stats.totals.reaction_tests.toLocaleString()}</span> reaction tests measured</span>
              {classic?.median && <span className="text-sm text-white/60">Median <span className="font-mono font-bold text-[#39FF14] text-base">{classic.median}ms</span></span>}
              {classic?.fastest && <span className="text-sm text-white/60">Fastest <span className="font-mono font-bold text-[#39FF14] text-base">{classic.fastest}ms</span></span>}
              <span className="text-sm text-[#39FF14] font-semibold inline-flex items-center gap-1">See live statistics <ChevronRight size={14} /></span>
            </Link>
          </section>
        );
      })()}

      {/* FEATURED GAMES STRIP */}
      <FeaturedGames />

      {/* EXPLORE MORE TESTS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Explore the Reaction Tests</h2>
            <p className="text-white/50 mt-2">Different tests for different kinds of speed.</p>
          </div>
          <Link to="/tests" className="hidden sm:flex items-center gap-1 text-[#39FF14] text-sm font-semibold hover:gap-2 transition-all">See all <ChevronRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <TestCard to="/test/classic" icon={Zap} title="Classic Reaction" desc="Simple visual reaction test. React when it turns green." />
          <TestCard to="/test/audio" icon={Volume2} title="Audio Reaction" desc="React to a sound instead of a color change." />
          <TestCard to="/test/gonogo" icon={Ban} title="Go / No-Go" desc="React to valid signals while ignoring the traps." />
        </div>
      </section>

      {/* COMPETE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-surface rounded-3xl p-8">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="text-[#39FF14]" size={22} />
              <h3 className="font-display font-bold text-xl">Global Leaderboard</h3>
            </div>
            {top.length ? (
              <div className="space-y-2">
                {top.map((r) => (
                  <div key={r.rank} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-white/40 w-6">#{r.rank}</span>
                      <span className="font-medium">{r.username}</span>
                    </div>
                    <span className="font-mono font-bold text-[#39FF14]">{r.reaction_ms}ms</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/40 text-sm">Be the first on the board — sign in and set a record.</p>
            )}
            <Link to="/leaderboard" className="inline-flex items-center gap-1 text-[#39FF14] text-sm font-semibold mt-5 hover:gap-2 transition-all">
              View full leaderboard <ChevronRight size={16} />
            </Link>
          </div>

          <div className="card-surface rounded-3xl p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Swords className="text-[#39FF14]" size={22} />
                <h3 className="font-display font-bold text-xl">Challenge a Friend</h3>
              </div>
              <p className="text-white/55 leading-relaxed">Beat a score, generate a unique challenge link, and see if your friends can react faster. Head-to-head, winner takes the bragging rights.</p>
            </div>
            <button onClick={() => navigate("/test/classic")} className="btn-glow bg-[#39FF14] text-black font-display font-extrabold px-6 py-3 rounded-full mt-6 w-fit">
              Set a score to challenge
            </button>
          </div>
        </div>
      </section>

      {/* IMPROVE + LEARN */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-6">
        <Link to="/dashboard" className="card-surface card-hover rounded-3xl p-8">
          <TrendingUp className="text-[#39FF14] mb-4" size={26} />
          <h3 className="font-display font-bold text-xl mb-2">Track your progress</h3>
          <p className="text-white/50">Personal dashboard with progress charts, records, streaks and your reaction score out of 1000.</p>
        </Link>
        <Link to="/learn" className="card-surface card-hover rounded-3xl p-8">
          <BookOpen className="text-[#39FF14] mb-4" size={26} />
          <h3 className="font-display font-bold text-xl mb-2">Learn the science</h3>
          <p className="text-white/50">Honest, careful guides on what reaction time is, what affects it, and how to actually get faster.</p>
        </Link>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-center mb-10">Questions & answers</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="card-surface rounded-2xl p-5 group" data-testid={`faq-${i}`}>
              <summary className="font-display font-semibold cursor-pointer list-none flex items-center justify-between">
                {f.q}<ChevronRight className="group-open:rotate-90 transition text-white/40" size={18} />
              </summary>
              <p className="text-white/55 mt-3 leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
